import React, { useState } from 'react';

import { FormItem, Dropdown, Card } from '../../../../components';
import { Input } from '../../../../components/Input';
import { validateJSONforInput } from '../../../../utilities/validateJSONforInput';
import type { AiModelData } from '../../../../api';
import type { PromptsFormType } from '../../../../pages/agent/hooks/useAgentPrompts';
import { PromptEditorModal } from './PromptEditorModal';
import { ReadonlyPromptInput } from './ReadonlyPromptInput';

type PromptFieldEditorProps = {
  name: keyof PromptsFormType;
  label: string;
  hint?: string;
  hideModelFields?: boolean;
  formValues: PromptsFormType;
  llmModels: AiModelData[];
  disabled?: boolean;
  updating?: boolean;
  onSave: (
    name: string,
    content: string,
    modelName: string | null,
    modelSettings: string,
  ) => void;
};

export const PromptFieldEditor: React.FC<PromptFieldEditorProps> = ({
  name,
  label,
  hint,
  hideModelFields,
  formValues,
  llmModels,
  disabled,
  updating,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const modelNameKey = `${name}_modelName` as keyof PromptsFormType;
  const modelSettingsKey = `${name}_modelSettings` as keyof PromptsFormType;

  const modelOptions: OptionType<string>[] = (llmModels ?? [])
    .filter(m => !!m.label)
    .map(m => ({ value: m.label, label: m.label }));

  const handleOpenEditor = () => {
    setIsEditing(true);
  };

  const handleSave = (
    content: string,
    modelName: string | null,
    modelSettings: string,
  ) => {
    if (hideModelFields) {
      onSave(name, content, null, '');
    } else {
      onSave(name, content, modelName, modelSettings);
    }
    setIsEditing(false);
  };

  const currentContent = (formValues[name] as string) ?? '';
  const currentModelName = (formValues[modelNameKey] as string | null) ?? null;
  const currentModelSettings = (formValues[modelSettingsKey] as string) ?? '';

  return (
    <Card
      collapsable
      size="unset"
      title={label}
      subtitle={hint}
      name={`agentpage.prompts.${name}`}
      withOverflow={false}
      contentClassName="flex flex-col gap-y-md"
    >
      <FormItem<PromptsFormType> name={name} label="Prompt" inline={true}>
        <ReadonlyPromptInput
          canEdit={!disabled && !updating}
          onEdit={handleOpenEditor}
        />
      </FormItem>

      {!hideModelFields && (
        <>
          <FormItem<PromptsFormType>
            name={modelNameKey as any}
            label="LLM Model"
            inline={true}
          >
            <Dropdown<string>
              options={modelOptions}
              disabled
              placeholder="Select LLM model"
            />
          </FormItem>

          <FormItem<PromptsFormType>
            name={modelSettingsKey as any}
            label="Model Settings"
            inline={true}
            validate={validateJSONforInput}
            errorBelowInput
          >
            <Input
              multiline
              disabled
              placeholder='{"key": "value"}'
              inputClassName="font-mono text-mainText text-medium"
            />
          </FormItem>
        </>
      )}

      {isEditing && (
        <PromptEditorModal
          initialContent={currentContent}
          initialModelName={currentModelName}
          initialModelSettings={currentModelSettings}
          modelOptions={modelOptions}
          hideModelFields={hideModelFields}
          onClose={() => setIsEditing(false)}
          onSave={handleSave}
        />
      )}
    </Card>
  );
};
